import { createElement, isValidElement } from 'react';
import { RiQuestionLine, type RemixiconComponentType } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { ICON_NAMES, ICON_REGISTRY, type IconName } from './lib/icon-registry';

/**
 * Named icons — a static registry (`lib/icon-registry.ts`, ~200 glyphs of spec §5.2/§5.3 + constructor widgets)
 * rendered synchronously, so stored names never flash. Components that only need a few glyphs should import
 * `RiXxx` directly; `<Icon name>` / `ICON_REGISTRY` are for names that come from data.
 */
export { ICON_NAMES, ICON_REGISTRY, type IconName };

/** A Remix icon component (`RiDropLine`), a registry name stored in data (`"drop-line"`) or a ready element. */
export type IconLike = RemixiconComponentType | IconName | (string & {}) | React.ReactElement;
export type IconComponent = RemixiconComponentType;

/** The glyph used when a stored name is not in the registry. */
export const FALLBACK_ICON: RemixiconComponentType = RiQuestionLine;

const warned = new Set<string>();

/** Resolve a registry name to its component (fallback glyph for unknown names). */
export function getIcon(name: string): RemixiconComponentType {
  const icon = Object.hasOwn(ICON_REGISTRY, name) ? (ICON_REGISTRY as Record<string, RemixiconComponentType>)[name] : undefined;
  if (!icon && import.meta.env.DEV && !warned.has(name)) {
    warned.add(name);
    console.warn(`[Icon] "${name}" is not in shared/ui/lib/icon-registry.ts — rendering the fallback glyph.`);
  }
  return icon ?? FALLBACK_ICON;
}

export function isIconName(name: string): name is IconName {
  return Object.hasOwn(ICON_REGISTRY, name);
}

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'ref' | 'fill' | 'color' | 'name' | 'children'> {
  /** Remix icon name, e.g. `"drop-line"` (see `ICON_NAMES`). Unknown names render a fallback glyph. */
  name: IconName | (string & {});
  /** Size in design px (1 Tailwind unit). Default 16. Pass a `size-*` class instead when you prefer. */
  size?: number;
  /** Accessible label; without it the icon is decorative (`aria-hidden`). */
  label?: string;
  /** Outline-only rendering (empty-state ghosts, spec §4.16): no fill, thin stroke in `currentColor`. */
  outline?: boolean;
}

/** Renders a Remix icon by its stored name: `<Icon name="drop-line" className="text-icon" />`. */
export function Icon({ name, size, label, outline, className, style, ...rest }: IconProps) {
  // a static registry lookup (not a component created during render) — createElement keeps lint happy
  return createElement(getIcon(name), {
    ...rest,
    ...(outline ? { color: 'none', stroke: 'currentColor', strokeWidth: 0.25 } : null),
    'data-icon': name,
    'aria-hidden': label ? undefined : true,
    'aria-label': label,
    role: label ? 'img' : undefined,
    className: cn('shrink-0', size == null && !/\bsize-/.test(className ?? '') && 'size-16', className),
    style: size != null ? { width: `${size / 16}rem`, height: `${size / 16}rem`, ...style } : style,
  } as React.ComponentProps<RemixiconComponentType>);
}

/**
 * Render any `IconLike` with the given classes — used by every component that takes an `icon` prop.
 * Elements are returned unchanged (the caller styles them).
 */
export function renderIcon(icon: IconLike | null | undefined, className?: string, extra?: { outline?: boolean }): React.ReactNode {
  if (icon == null) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === 'string') return <Icon name={icon} className={className} outline={extra?.outline} />;
  return createElement(icon as RemixiconComponentType, {
    'aria-hidden': true,
    className: cn('shrink-0', className),
    ...(extra?.outline ? { color: 'none', stroke: 'currentColor', strokeWidth: 0.25 } : null),
  });
}
